import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../redux/hook";
import { SightedIOC } from "../../models/ReportModel";
import { ResponsiveLine, Serie } from "@nivo/line";
import { msalInstance } from "../../index";
import { useAppDispatch } from "../../redux/hook";
import { getIocTrendsData } from "../../service/apiAccessLogic";
import { mergeIocTrendsWeek, mergeIocTrendsMonth, resetIocTrends } from "../../redux/sherlockSlice";
import { buildKey, requestMemo } from "../utility/RequestMemo";

/**
 * Combined IOC Rank card:
 * - Customer filter (All + per-customer)
 * - Period filter (Week / Month)
 * - Single table (top IOCs) + multi-series line chart of selected IOCs
 * - Checkboxes per IOC to toggle each series
 */

// ---- Types for trends ----
type Point = { x: string; y: number };
type TrendSeries = Point[];
type TrendSeriesMap = Record<
    string,      // indicator (base64 key coming from backend)
    TrendSeries  // [{x, y}, ...]
>;
type CustomerTrendMap = Record<
    string,          // customer
    TrendSeriesMap   // indicator -> series
>;

function decodeIndicator(id: string) {
    try {
        return atob(id);
    } catch {
        return id;
    }
}

// Build a minimal, nice-looking zero baseline so the chart area/layout is stable with no data.
function baselineZeroes(days = 7): Point[] {
    const out: Point[] = [];
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(now.getDate() - i);
        const label = d.toISOString().slice(0, 10);
        out.push({ x: label, y: 0 });
    }
    return out;
}

/**
 * Merge all customers' trend maps by summing y-values per indicator/date.
 * NOTE: Input is a single map of customer -> (indicator -> series)
 */
function mergeCustomersTrends(trendByCustomer?: CustomerTrendMap): TrendSeriesMap {
    const out: TrendSeriesMap = {};
    if (!trendByCustomer) return out;

    for (const trendMap of Object.values(trendByCustomer)) {
        // trendMap: indicator(b64) -> series
        for (const [indB64, series] of Object.entries(trendMap || {})) {
            const ind = decodeIndicator(indB64);

            const existing = out[ind] || [];
            const accByX = new Map<string, number>();

            // Seed with existing
            for (const pt of existing) accByX.set(pt.x, (accByX.get(pt.x) || 0) + pt.y);
            // Add this customer’s series
            for (const pt of (series || [])) accByX.set(pt.x, (accByX.get(pt.x) || 0) + (pt.y || 0));

            out[ind] = Array.from(accByX.entries())
                .map(([x, y]) => ({ x, y }))
                .sort((a, b) => a.x.localeCompare(b.x));
        }
    }
    return out;
}

/**
 * Decode a single customer's trend map (indicator keys) into plain-text indicators.
 */
function decodeTrendMap(m?: TrendSeriesMap): TrendSeriesMap {
    const out: TrendSeriesMap = {};
    if (!m) return out;
    for (const [ind, series] of Object.entries(m)) {
        out[decodeIndicator(ind)] = (series || []).map((p: Point) => ({ x: p.x, y: p.y }));
    }
    return out;
}

// helper to compute [start,end] from period (inclusive date strings)
function computeRange(period: "week" | "month"): { start: string; end: string } {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - (period === "week" ? 6 : 29));
    const toStr = (d: Date) => d.toISOString().slice(0, 10);
    return { start: toStr(start), end: toStr(end) };
}

const IndicatorMetrics = () => {
    const lastWeekIOCsRank = useAppSelector(
        (state) => state.sherlock.lastWeekIOCsRank
    ) as Record<string, SightedIOC[]> | undefined;

    const lastMonthIOCsRank = useAppSelector(
        (state) => state.sherlock.lastMonthIOCsRank
    ) as Record<string, SightedIOC[]> | undefined;

    const iocTrendsWeek = useAppSelector(
        (s) => (s as any).sherlock.iocTrendsWeek
    ) as CustomerTrendMap | undefined;

    const iocTrendsMonth = useAppSelector(
        (s) => (s as any).sherlock.iocTrendsMonth
    ) as CustomerTrendMap | undefined;

    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();

    // --------- Filters ----------
    const customers = useMemo(() => {
        const set = new Set<string>();
        if (lastWeekIOCsRank) Object.keys(lastWeekIOCsRank).forEach((k) => set.add(k));
        if (lastMonthIOCsRank) Object.keys(lastMonthIOCsRank).forEach((k) => set.add(k));
        return Array.from(set).sort();
    }, [lastWeekIOCsRank, lastMonthIOCsRank]);

    const [selectedCustomer, setSelectedCustomer] = useState<string>("All");
    const [period, setPeriod] = useState<"week" | "month">("week");

    // --------- Table data (ranks) ----------
    const aggregateRanks = (
        ranks?: Record<string, SightedIOC[]>,
        customer?: string
    ) => {
        if (!ranks) return [] as { indicator: string; count: number }[];

        let source: SightedIOC[] = [];
        if (!customer || customer === "All") {
            Object.values(ranks).forEach((arr) => {
                source.push(...arr);
            });
        } else {
            source = ranks[customer] ?? [];
        }

        const grouped = source.reduce((acc, ioc) => {
            const indicator = decodeIndicator(ioc.indicator);
            acc[indicator] = (acc[indicator] || 0) + ioc.count;
            return acc;
        }, {} as Record<string, number>);

        return Object.entries(grouped)
            .map(([indicator, count]) => ({ indicator, count }))
            .sort((a, b) => b.count - a.count);
    };

    const rows = useMemo(() => {
        return period === "week"
            ? aggregateRanks(lastWeekIOCsRank, selectedCustomer)
            : aggregateRanks(lastMonthIOCsRank, selectedCustomer);
    }, [period, selectedCustomer, lastWeekIOCsRank, lastMonthIOCsRank]);

    // --------- Chart data ----------
    // Build trend series for the same scope (period + customer).
    const seriesByIndicator: TrendSeriesMap = useMemo(() => {
        const trendsForPeriod = period === "week" ? iocTrendsWeek : iocTrendsMonth;

        if (selectedCustomer === "All") {
            // Merge all customers into decoded indicator keys
            return mergeCustomersTrends(trendsForPeriod);
        } else {
            // Decode keys for one customer's map
            const customerMap = trendsForPeriod?.[selectedCustomer];
            return decodeTrendMap(customerMap);
        }
    }, [period, selectedCustomer, iocTrendsWeek, iocTrendsMonth]);

    // Choose top-N to render checkboxes by default
    const TOP_N = 10;
    const DEFAULT_CHECKED = 5;

    // Checkbox state keyed by indicator string
    const [checked, setChecked] = useState<Record<string, boolean>>({});

    const requestedIndicatorsB64 = useMemo(() => {
        const encode = (s: string) => btoa(s);
        const top = rows.slice(0, TOP_N).map(r => r.indicator);
        const toggledOn = Object.entries(checked).filter(([, v]) => v).map(([k]) => k);
        const set = new Set<string>([...top, ...toggledOn]);
        return Array.from(set).map(encode);
    }, [rows, checked]);

    // When rows change (filters), initialize defaults: top N shown, top DEFAULT_CHECKED checked.
    useEffect(() => {
        const initial: Record<string, boolean> = {};
        rows.slice(0, TOP_N).forEach((r, idx) => {
            initial[r.indicator] = idx < DEFAULT_CHECKED;
        });
        setChecked(initial);
    }, [rows]);

    useEffect(() => {
        if (!requestedIndicatorsB64.length) return;

        const { start, end } = computeRange(period);
        const customersParam = selectedCustomer === "All" ? "all" : [selectedCustomer];

        // Build a stable key for memo
        const key = buildKey({
            type: "iocTrends",
            period,
            start,
            end,
            customers: customersParam === "all" ? ["all"] : (customersParam as string[]),
            indicators: requestedIndicatorsB64,
        });
        
        const TTL_MS = 3 * 60 * 1000;

        requestMemo
            .run(key, TTL_MS, () =>
                getIocTrendsData(msalInstance, {
                    period,
                    start,
                    end,
                    customers: customersParam as any,
                    indicators: requestedIndicatorsB64,
                })
            )
            .then((res: any) => {
                setLoading(false);
                if (!res) return; // suppressed by TTL
                const payload = res.data?.data; // Record<customerId, Record<b64Indicator, {x,y}[]>>
                if (!payload) return;

                if (period === "week") {
                    dispatch(mergeIocTrendsWeek(payload));
                } else {
                    dispatch(mergeIocTrendsMonth(payload));
                }
            })
            .catch((err: any) => {
                console.error("Error fetching IOC trends", err);
            });
        setLoading(true);
        dispatch(resetIocTrends());
        // Include primitives & joined arrays for stable deps
    }, [period, selectedCustomer, requestedIndicatorsB64.join("|")]);

    // Build Nivo series from checked indicators + trend data
    const chartSeries: Serie[] = useMemo(() => {
        const activeIndicators = rows
            .slice(0, TOP_N)
            .map((r) => r.indicator)
            .filter((ind) => checked[ind]);

        const s: Serie[] = [];

        if (Object.keys(seriesByIndicator).length > 0) {
            for (const ind of activeIndicators) {
                const pts = seriesByIndicator[ind] || [];
                if (pts.length) {
                    s.push({
                        id: ind,
                        data: pts.map((p) => ({ x: p.x, y: p.y })),
                    });
                }
            }
        } else {
            // No trend data yet: create a baseline so the UI feels consistent.
            for (const ind of activeIndicators) {
                s.push({
                    id: ind,
                    data: baselineZeroes(period === "week" ? 7 : 30),
                });
            }
        }

        return s.length
            ? s
            : [
                {
                    id: "No selection",
                    data: baselineZeroes(period === "week" ? 7 : 30),
                },
            ];
    }, [rows, checked, seriesByIndicator, period]);

    const hasAnyRankData =
        (lastWeekIOCsRank && Object.keys(lastWeekIOCsRank).length > 0) ||
        (lastMonthIOCsRank && Object.keys(lastMonthIOCsRank).length > 0);

    // Build a trimmed x-axis tick list to reduce clutter
    const tickValuesToShow = useMemo(() => {
        const first = chartSeries[0];
        const pts = (first?.data as any[]) ?? [];
        if (!pts.length) return [];
        return pts.length > 10
            ? pts.filter((_, i) => i % 2 === 0).map((p: any) => String(p.x))
            : pts.map((p: any) => String(p.x));
    }, [chartSeries]);

    return (
        <div className="rounded-lg bg-white shadow-sm border border-gray-200 p-4">
            {/* Header & Filters */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
                <span className="text-sm font-medium">IOC Trends</span>
                <div className="flex flex-wrap items-center gap-3">
                    {loading && (
                        <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-600"></div>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <label htmlFor="period-filter" className="text-sm text-gray-600">
                            Period:
                        </label>
                        <select
                            id="period-filter"
                            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value as "week" | "month")}
                        >
                            <option value="week">Week</option>
                            <option value="month">Month</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <label htmlFor="customer-filter" className="text-sm text-gray-600">
                            Customer:
                        </label>
                        <select
                            id="customer-filter"
                            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                            value={selectedCustomer}
                            onChange={(e) => setSelectedCustomer(e.target.value)}
                        >
                            <option value="All">All</option>
                            {customers.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {!hasAnyRankData ? (
                <div className="h-24 grid place-items-center text-sm text-gray-500">
                    No IOC rank data available.
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Left: Table + checkboxes */}
                    <div className="lg:col-span-1 rounded-lg border border-gray-200">
                        <div className="px-3 py-2 border-b text-sm font-medium">
                            Top IOCs ({period === "week" ? "Last week" : "Last month"})
                        </div>
                        <div className="max-h-[420px] overflow-auto p-3">
                            {rows.length === 0 ? (
                                <div className="h-20 grid place-items-center text-sm text-gray-500">
                                    No results for this selection
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-600">
                                            <th className="py-2 pr-4">Show</th>
                                            <th className="py-2 pr-4">Indicator</th>
                                            <th className="py-2 pr-0 text-right">Count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.slice(0, 10).map((row) => (
                                            <tr key={row.indicator} className="border-t">
                                                <td className="py-2 pr-4 align-middle">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4"
                                                        checked={!!checked[row.indicator]}
                                                        onChange={(e) =>
                                                            setChecked((prev) => ({
                                                                ...prev,
                                                                [row.indicator]: e.target.checked,
                                                            }))
                                                        }
                                                    />
                                                </td>
                                                <td className="py-2 pr-4 whitespace-nowrap">{row.indicator}</td>
                                                <td className="py-2 pr-0 text-right tabular-nums">{row.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="px-3 py-2 border-t text-xs text-gray-600">
                            Showing top {Math.min(rows.length, 10)} IOCs
                        </div>
                    </div>

                    {/* Right: Multi-series line chart */}
                    <div className="lg:col-span-2 rounded-lg border border-gray-200 p-3 h-[420px]">
                        <div className="h-[95%] w-full">
                            <ResponsiveLine
                                data={chartSeries}
                                theme={{
                                    text: { fontSize: 12, fill: "#374151" },
                                    axis: {
                                        ticks: { text: { fill: "#6B7280" } },
                                        legend: { text: { fill: "#6B7280" } },
                                    },
                                    grid: { line: { stroke: "#E5E7EB", strokeWidth: 1 } },
                                    crosshair: { line: { stroke: "#9CA3AF", strokeWidth: 1, strokeOpacity: 0.6 } },
                                    tooltip: { container: { fontSize: 12, borderRadius: 8 } },
                                }}
                                margin={{ top: 8, right: 12, bottom: 28, left: 40 }}
                                xScale={{ type: "point" }}
                                yScale={{ type: "linear", min: 0, max: "auto", stacked: false, nice: true }}
                                axisBottom={{
                                    tickSize: 0,
                                    tickPadding: 8,
                                    tickRotation: 0,
                                    format: (v) =>
                                        tickValuesToShow.includes(String(v))
                                            ? String(v).slice(5) // show MM-DD
                                            : "",
                                }}
                                axisLeft={{
                                    tickSize: 0,
                                    tickPadding: 6,
                                    tickRotation: 0,
                                    format: (v) =>
                                        Number(v) >= 1000 ? `${Math.round(Number(v) / 1000)}k` : String(v),
                                }}
                                enableGridX={false}
                                enableGridY={true}
                                curve="monotoneX"
                                enablePoints={false}
                                pointSize={0}
                                enableSlices="x"
                                useMesh={true}
                                animate={false}
                                motionConfig="gentle"
                                enableArea={true}
                                areaOpacity={0.15}
                                // Add vertical line marker for start of current day
                                markers={[
                                    {
                                        axis: 'x',
                                        value: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
                                        lineStyle: { stroke: '#a0a0a0ff', strokeWidth: 1, strokeDasharray: '4 4' },
                                        legend: 'Today starts',
                                        legendOrientation: 'vertical',
                                        legendPosition: 'top-right',
                                        textStyle: {
                                            fill: '#a0a0a0ff',
                                            fontSize: 10
                                        }
                                    }
                                ]}
                                tooltip={({ point }) => (
                                    <div className="rounded-md border border-gray-200 bg-white px-2 py-1 shadow-sm">
                                        <div className="text-[11px] text-gray-600">{String(point.data.xFormatted || point.data.x)}</div>
                                        <div className="text-xs text-gray-700">{String(point.serieId)}</div>
                                        <div className="text-sm font-semibold tabular-nums">
                                            {point.data.yFormatted ?? point.data.y}
                                        </div>
                                    </div>
                                )}
                            />
                            <div className="w-full pt-2 text-center text-xs text-gray-500 italic">
                                Note: Today's data may be incomplete and should be interpreted accordingly.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IndicatorMetrics;
